import React, { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useParams, useNavigate, Navigate } from "react-router-dom";
import AdminLayout from "../components/layout/AdminLayout";
import { AdminRoleGuard } from "../components/layout/AdminRoleGuard";
import { SuperUserGuard } from "../components/layout/SuperUserGuard";
import { BizAdminRoleGuard } from "../components/layout/BizAdminRoleGuard";
import { SubscriptionGuard } from "@/components/auth/SubscriptionGuard";
import ClassManagementDashboard from '@/components/classes/dashboard/ClassManagementDashboard';
import GettingStartedPage from '../components/onboarding/OnboardingModal';
// Direct imports for all other page components
import Families from "../pages/Families";
import FamilyDetail from "../pages/FamilyDetail";
import Students from "../pages/Students";
import Schedule from "../pages/Schedule";
import Reporting from "../pages/Reporting";
import SubscriptionManagement from "../pages/SubscriptionManagement";
import SubscriptionCenterPage from "../pages/admin/subscription-center";
import SubscriptionSuccessPage from "../pages/admin/subscription-success";
import UpdateSubscriptionSuccessPage from "../pages/admin/update-subscription-success";
import WidgetTestPage from "../pages/admin/WidgetTestPage";
import Apparel from "../pages/Apparel";
import Staff from "../pages/Staff";
// Lazy-load only Dashboard
const DashboardAI = React.lazy(() => import("../pages/DashboardAI"));
import StripeConnectRequests from "../pages/admin/StripeConnectRequests";
import Messages from "../pages/Messages";
import AIContentGenerator from "../pages/AIContentGenerator";
import ApparelManagement from "../pages/ApparelManagement";
import ApparelShop from "../pages/ApparelShop";
import ApparelOrders from "../pages/ApparelOrders";
import Help from "../pages/Help";
import FeatureRequests from "../pages/FeatureRequests";
import TestScribe from "../pages/TestScribe";
import ParentPortalSettings from "../pages/admin/ParentPortalSettings";
import ParentDocuments from "../pages/admin/ParentDocuments";
import DocumentRequests from "../pages/admin/DocumentRequests";
import FormTemplates from "../pages/admin/FormTemplates";
import DocumentManagement from "../pages/admin/DocumentManagement";
import Notifications from "@/pages/admin/Notifications";
import ParentInviteTest from "@/pages/admin/ParentInviteTest";
import Hive360Settings from "../pages/admin/Hive360Settings";
import Unauthorized from "../pages/Unauthorized";
import NotFound from "../pages/NotFound";
import UserGuide from "../pages/UserGuide";
import DataImport from "../pages/admin/DataImport";
import TestLayout from "../pages/TestLayout";
import TestSequentialImport from "../pages/TestSequentialImport";
import MakeupClasses from "../pages/admin/MakeupClasses";
import StudentDetail from "../pages/StudentDetail";
import ClassDetail from "../pages/ClassDetail";
import Seasons from "../pages/Seasons";
import Invoices from "../pages/Invoices";
import PaymentsDashboard from "../pages/PaymentsDashboard";
import BillingSchedules from "../pages/admin/BillingSchedules";
import Settings from "../pages/Settings";
import WhatsNew from "../pages/WhatsNew";
import MaterialDashboard from "../pages/MaterialDashboard";
import HiveShareManagement from "../pages/HiveShareManagement";
import LiveStreamManagement from "../pages/LiveStreamManagement";
import BillAssistPage from "../pages/BillAssistPage";
import HivePad from "../pages/HivePad";

// Loading component for suspended routes
const LazyLoadingSpinner = () => (
  <div className="flex justify-center items-center h-full py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Helper component to handle legacy class routes
const LegacyClassRouteHandler = ({ baseRoute }) => {
  const params = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // If there's an ID parameter, redirect to the class detail page
    if (params.id) {
      navigate(`/admin/classes/${params.id}`, { replace: true });
    } else {
      // Otherwise redirect to the classes list
      navigate('/admin/classes', { replace: true });
    }
  }, [params, navigate]);
  
  return <LazyLoadingSpinner />;
};

interface AdminRoutesProps {
  baseRoute?: string;
}

export default function AdminRoutes({ baseRoute }: AdminRoutesProps = {}) {
  // Add debug logging
  console.log("AdminRoutes rendering with baseRoute:", baseRoute);
  
  // If this is a legacy route (like /classes), handle it appropriately
  if (baseRoute === "classes") {
    return <LegacyClassRouteHandler baseRoute={baseRoute} />;
  }
  
  return (
    <AdminRoleGuard>
      <SubscriptionGuard>
        <Routes>
          {/* All admin routes with AdminLayout wrapper */}
          <Route element={<AdminLayout />}>
          <Route index element={<Suspense fallback={(function(){console.log("AdminRoutes: Suspense fallback shown"); return <div>Loading dashboard…</div>;})()}><DashboardAI /></Suspense>} />
          {/* Material Dashboard with enhanced features */}
          <Route path="dashboard-material" element={<MaterialDashboard />} />
          <Route path="families" element={<Families />} />
          <Route path="families/:id" element={<FamilyDetail />} />
          <Route path="students" element={<Students />} />
          <Route path="students/:studentId" element={<Suspense fallback={<LazyLoadingSpinner />}><StudentDetail /></Suspense>} />
          <Route path="classes" element={<ClassManagementDashboard />} />
          <Route path="classes/:id" element={<Suspense fallback={<LazyLoadingSpinner />}><ClassDetail /></Suspense>} />
          <Route path="makeup-classes" element={<Suspense fallback={<LazyLoadingSpinner />}><MakeupClasses /></Suspense>} />
          <Route path="schedule" element={<Schedule />} />
          <Route path="seasons" element={<Suspense fallback={<LazyLoadingSpinner />}><Seasons /></Suspense>} />
          <Route path="invoices" element={<Suspense fallback={<LazyLoadingSpinner />}><Invoices /></Suspense>} />
          <Route path="bill-assist" element={<Suspense fallback={<LazyLoadingSpinner />}><BillAssistPage /></Suspense>} />
          <Route path="payments-dashboard" element={<Suspense fallback={<LazyLoadingSpinner />}><PaymentsDashboard /></Suspense>} />
          <Route path="billing-schedules" element={<Suspense fallback={<LazyLoadingSpinner />}><BillingSchedules /></Suspense>} />
          <Route path="messages" element={<Suspense fallback={<LazyLoadingSpinner />}><Messages /></Suspense>} />
          <Route path="messages/:conversationId" element={<Suspense fallback={<LazyLoadingSpinner />}><Messages /></Suspense>} />
          <Route path="hivepad" element={<Suspense fallback={<LazyLoadingSpinner />}><HivePad /></Suspense>} />
          <Route path="ai-content-generator" element={<Suspense fallback={null}><AIContentGenerator /></Suspense>} />
          <Route path="reporting" element={<Reporting />} />
          <Route path="subscription-management" element={
            <SuperUserGuard>
              <SubscriptionManagement />
            </SuperUserGuard>
          } />
          <Route path="settings/subscription-center" element={
            <BizAdminRoleGuard>
              <SubscriptionCenterPage />
            </BizAdminRoleGuard>
          } />
          <Route path="subscription-center" element={<SubscriptionCenterPage />} />
          <Route path="widget-test" element={<WidgetTestPage />} />
          <Route path="settings/hive360" element={<Hive360Settings />} />
          <Route path="subscription-success" element={
            <BizAdminRoleGuard>
              <SubscriptionSuccessPage />
            </BizAdminRoleGuard>
          } />
          <Route path="update-subscription-success" element={
            <BizAdminRoleGuard>
              <UpdateSubscriptionSuccessPage />
            </BizAdminRoleGuard>
          } />
          <Route path="apparel" element={<Apparel />} />
          <Route path="apparel/*" element={<Apparel />} />
          <Route path="apparel/manage" element={<Suspense fallback={<LazyLoadingSpinner />}><ApparelManagement /></Suspense>} />
          <Route path="apparel/orders" element={<Suspense fallback={<LazyLoadingSpinner />}><ApparelOrders /></Suspense>} />
          <Route path="staff" element={<Staff />} />
          <Route path="settings" element={<Suspense fallback={<LazyLoadingSpinner />}><Settings /></Suspense>} />
          <Route path="whats-new" element={<WhatsNew />} />
          <Route path="data-import" element={<Suspense fallback={<LazyLoadingSpinner />}><DataImport /></Suspense>} />
              <Route path="test-sequential-import" element={<Suspense fallback={<LazyLoadingSpinner />}><TestSequentialImport /></Suspense>} />
          <Route path="help" element={<Suspense fallback={<LazyLoadingSpinner />}><Help /></Suspense>} />
          <Route path="help/feature-requests" element={<Suspense fallback={<LazyLoadingSpinner />}><FeatureRequests /></Suspense>} />
          <Route path="test-scribe" element={<Suspense fallback={<LazyLoadingSpinner />}><TestScribe /></Suspense>} />
          <Route path="test-layout" element={<TestLayout />} />
          <Route path="hiveshare" element={<Suspense fallback={<LazyLoadingSpinner />}><HiveShareManagement /></Suspense>} />
          <Route path="live-stream" element={<Suspense fallback={<LazyLoadingSpinner />}><LiveStreamManagement /></Suspense>} />
          <Route path="stripe-connect-requests" element={<Suspense fallback={<LazyLoadingSpinner />}><StripeConnectRequests /></Suspense>} />
          <Route path="parent-portal-settings" element={<Suspense fallback={<LazyLoadingSpinner />}><ParentPortalSettings /></Suspense>} />
          <Route path="document-management" element={<Suspense fallback={<LazyLoadingSpinner />}><DocumentManagement /></Suspense>} />
          <Route path="parent-documents" element={<Suspense fallback={<LazyLoadingSpinner />}><ParentDocuments /></Suspense>} />
          <Route path="document-requests" element={<Suspense fallback={<LazyLoadingSpinner />}><DocumentRequests /></Suspense>} />
          <Route path="form-templates" element={<Suspense fallback={<LazyLoadingSpinner />}><FormTemplates /></Suspense>} />
          <Route path="user-guide" element={<Suspense fallback={<LazyLoadingSpinner />}><UserGuide /></Suspense>} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="parent-invite-test" element={<Suspense fallback={<LazyLoadingSpinner />}><ParentInviteTest /></Suspense>} />
          <Route path="getting-started" element={<GettingStartedPage />} />
          <Route path="unauthorized" element={<Suspense fallback={<LazyLoadingSpinner />}><Unauthorized /></Suspense>} />
          <Route path="*" element={<Suspense fallback={<LazyLoadingSpinner />}><NotFound /></Suspense>} />
        </Route>
        </Routes>
      </SubscriptionGuard>
    </AdminRoleGuard>
  );
}
